/**
 * MONSTER TOWN — WHERE THE GUIDE ARROW POINTS. ERRAND CREW.
 *
 * A step in `src/content/errands.ts` carries `at: 'town' | 'garden' |
 * 'house_kitchen' | 'venue_zoo' | 'pond'` — a DATA key, deliberately not a
 * position, so an errand can be written before the place it names exists.
 * Turning that key into a point on the glass is this file's whole job.
 *
 * CREWS NEVER IMPORT EACH OTHER, so nothing here reaches into `src/world/**`.
 * Four sources, all of them already-published contracts:
 *   1. `world.portalsIn(activeScene)` — core knows every door out of here.
 *   2. `world:landmarksRequest` -> `world:landmarks` — the world crew's own
 *      request/response pair, replied to SYNCHRONOUSLY (see hotspots.ts).
 *   3. `world:sceneryRequest` -> `world:scenery` — same shape, for the tappable
 *      things standing about (the fountain, the plaza, benches, lamp posts).
 *   4. THE THINGS ACTUALLY STANDING IN THE ROOM — `world.get(scene).grid.all()`
 *      plus `getPlaceable().tags`, both of them core reads every crew may make.
 *      See "WHY A PLACED THING IS A PLACE" below.
 *
 * ===========================================================================
 *  AN `at` MAY BE A CHAIN: 'house_kitchen>cook_pot' (fixed vr2 — MAJOR)
 * ===========================================================================
 * "Go to the kitchen" and "tap the pot" are the same instruction at two
 * distances, and a kid needs whichever one they are far enough away to need.
 * The reference errand's `make` step said `at: 'house_kitchen'`, which walks a
 * kid two doors across town beautifully (measured: two taps from spawn to
 * inside the house, two more to the kitchen door) and then goes SILENT the
 * moment they arrive — rung (a) below answers `here`, `at` is null, and
 * `paintGuide` hides the arrow. The kitchen has twelve placed things in it and
 * nothing on the glass says which one is the pot. **The one instruction a
 * pre-reader has ran out one object short of the goal**, and the same shape
 * applied to `deliver ... at: 'venue_zoo'` the moment they walked into the zoo.
 *
 * So `at` may name a ROUTE: rungs separated by `>`, tried left to right, and
 * the first one that answers with something to point AT wins. A rung that
 * answers `here` means *you have arrived at this one, look further in*; a rung
 * that answers nothing at all is skipped, so a chain naming an unshipped place
 * degrades to the rest of itself. A chainless key behaves exactly as it always
 * did — this is additive, and it is DATA: a future step that ends where the kid
 * is standing adds `>the_thing` in `src/content/errands.ts` and nothing here
 * changes.
 *
 * RESOLUTION ORDER FOR ONE RUNG, and it is a ladder, not a guess:
 *   a) the key IS the scene you are standing in            -> 'here', no arrow
 *   b) the key is another scene that exists                -> the DOOR to it
 *   c) the key names a landmark/scenery id in this scene    -> that thing
 *   d) the key names a PLACED THING — its def id, or one of its `tags`
 *   e) the key is a prefix of one ('bench' -> 'bench:plaza0')-> the nearest one
 *   f) the same three rungs again for each SYNONYM, IN ORDER
 *   g) nothing                                              -> NO ARROW, quietly
 *
 * (g) IS A FEATURE. An errand may name a place the crew that owns it has not
 * shipped yet; the card still shows the pictures, the errand still waits
 * forever, and no arrow points at nowhere. A wrong arrow is worse than none —
 * a three-year-old follows it.
 *
 * ===========================================================================
 *  WHY A PLACED THING IS A PLACE (fixed vr1 — this was a BLOCKER)
 * ===========================================================================
 * `at: 'garden'` used to resolve, through the synonym list below, to the
 * nearest `bloom:*` — a DECORATIVE flower bed 344 px away from
 * `cook_fruit_tree`, which is the object that actually gives you `ing_fruit`.
 * The critic drove fourteen consecutive hops tapping exactly where the arrow
 * pointed; `have` stayed 0/3 every time and the arrow oscillated between two
 * blooms as the monster walked between them. A pre-reader has exactly one
 * instruction — go where the arrow is — so that was an infinite loop.
 *
 * The pickables were never the problem: `cook_fruit_tree` has carried
 * `tags: ['garden', 'starter', 'cooking', 'source']` since the day it shipped.
 * This file simply never looked at what was standing in the room. It does now,
 * and because it matches TAGS, "where the fruit comes from" stays a data drop:
 * a new pickable tagged `garden` is findable the day it lands.
 *
 * ===========================================================================
 *  THE SYNONYM LIST IS ORDERED, AND THAT IS LOAD-BEARING
 * ===========================================================================
 * It used to be one flat bag of words matched in a single nearest-wins sweep,
 * so a decorative bloom two metres away beat the real answer twelve metres
 * away. Each word is now tried IN TURN and the first word that finds anything
 * wins outright — distance only ever breaks a tie WITHIN one word. Put the
 * thing you actually mean first.
 */

import { bus } from '../core/events'
import { getPlaceable } from '../core/placeable'
import { world } from '../core/scene'
import type { PlaceableId, SceneId, Vec3 } from '../core/types'

/**
 * SYNONYMS — pure data, and ORDERED (see the header). An errand written against
 * a place-word the world crew spells differently. Add a line; never a code
 * change.
 */
const SYNONYM: Readonly<Record<string, readonly string[]>> = {
  // A garden is where things GROW before it is where things are pretty: the
  // pickables (tagged `garden`) are matched by the key itself at rung (d), and
  // these two are the fallback for a town that has none.
  garden: ['bloom', 'flower'],
  plaza: ['plaza', 'fountain'],
  home: ['door:house'],
  house: ['door:house'],
  shop: ['door:shop'],
  restaurant: ['door:restaurant'],
  post: ['mailbox'],
}

export type GuideKind = 'here' | 'place' | 'door'

export interface GuideTarget {
  readonly kind: GuideKind
  /** World point to draw the arrow above. Null only for `here`. */
  readonly at: Vec3 | null
  /** The scene the arrow is drawn in — always the ACTIVE one. */
  readonly sceneId: SceneId | null
  /** The landmark id, when the arrow is standing on a real door. */
  readonly landmarkId: string | null
}

interface Spot {
  id: string
  at: Vec3
  toScene?: string
  /** Only placed things carry these — a def's own `tags`. See the header. */
  tags?: readonly string[]
}

let cachedScene: SceneId | null = null
let places: Spot[] = []
/** Rung (d): everything a kid (or the starter kit) has actually put down here. */
let props: Spot[] = []

/**
 * Ask the world crew what is standing here. Replies are synchronous.
 *
 * ===========================================================================
 *  THE WORLD CREW ALWAYS ANSWERS ABOUT THE TOWN (fixed vr2 — MAJOR)
 * ===========================================================================
 * `world:landmarksRequest` and `world:sceneryRequest` are the town's lists and
 * they are not scoped to the awake scene — measured from inside venue #3 with
 * `tools/fix-int-r2-probe.mjs --which arrow`: `__errands.places()` came back
 * full of `bench:plaza0`, `bloom:-11.4:1z` and the rest of the TOWN's furniture
 * while the kid was standing in the zoo.
 *
 * That is not cosmetic. `exitsFrom` trusts those landmarks as ways out, so a
 * `deliver` step pointing at `town` routed "zoo -> the ice cream shop's front
 * door -> town" and handed the guide arrow a point in the TOWN, which projected
 * to x = 3346 on a 1024-wide iPad. The arrow did the only thing it can do with
 * a target 2300 px off the right-hand edge: it lay down on the rim and pointed
 * up into the camera-rotate cluster, which is exactly the picture the critic
 * filed. A pre-reader has one instruction, and it was aimed at a button.
 *
 * A landmark list with no scene on it can only be trusted in the scene it is
 * about, so indoors it is not asked for at all. What replaces it is `roomExits`
 * — from inside a room, everywhere else in the world is through the door, and
 * the door is a thing that really is on this glass.
 */
function refresh(): void {
  const active = world.activeSceneId
  places = []
  props = []
  const node0 = active === null ? undefined : world.get(active)
  const outdoors = node0 !== undefined && node0.def.kind === 'exterior'

  let landmarks: Spot[] = []
  if (!outdoors) {
    const node = node0
    if (node !== undefined) {
      for (const p of node.grid.all()) {
        const def = getPlaceable(p.defId as PlaceableId)
        props.push({
          id: String(p.defId),
          at: node.grid.cellToWorld(p.anchor),
          ...(def?.tags === undefined ? {} : { tags: def.tags }),
        })
      }
    }
    cachedScene = active
    return
  }
  const offL = bus.on('world:landmarks', (e) => {
    landmarks = e.list.map((l) => ({
      id: l.id,
      at: { x: l.at.x, y: l.at.y, z: l.at.z },
      ...(l.toScene === undefined ? {} : { toScene: l.toScene }),
    }))
  })
  bus.emit('world:landmarksRequest', {})
  offL()

  let scenery: Spot[] = []
  const offS = bus.on('world:scenery', (e) => {
    scenery = e.list.map((s) => ({ id: s.id, at: { x: s.at.x, y: s.at.y, z: s.at.z } }))
  })
  bus.emit('world:sceneryRequest', {})
  offS()

  places = [...landmarks, ...scenery]

  /*
   * THE THINGS STANDING IN THE ROOM. Built once per scene (and dropped whenever
   * the town changes), not per projection — a town is a few dozen placements and
   * the arrow re-aims eight times a second.
   */
  const node = active === null ? undefined : world.get(active)
  if (node !== undefined) {
    for (const p of node.grid.all()) {
      const def = getPlaceable(p.defId as PlaceableId)
      props.push({
        id: String(p.defId),
        at: node.grid.cellToWorld(p.anchor),
        ...(def?.tags === undefined ? {} : { tags: def.tags }),
      })
    }
  }
  cachedScene = active
}

function ensure(): void {
  if (cachedScene !== world.activeSceneId || (places.length === 0 && props.length === 0)) {
    refresh()
  }
}

/** Nearest matching spot to a reference point, so 'bench' means the near bench. */
function nearestOf(
  list: readonly Spot[],
  match: (s: Spot) => boolean,
  from: Vec3 | null,
): Spot | null {
  let best: Spot | null = null
  let bestD = Infinity
  for (const p of list) {
    if (!match(p)) continue
    const d = from === null ? 0 : Math.hypot(p.at.x - from.x, p.at.z - from.z)
    if (d < bestD) {
      bestD = d
      best = p
    }
  }
  return best
}

function nearest(match: (id: string) => boolean, from: Vec3 | null): Spot | null {
  return nearestOf(places, (s) => match(s.id), from)
}

/**
 * ONE WORD, DOWN THE LADDER. Exact scenery, then a placed thing (by def id or
 * by tag), then a loose prefix/suffix on a scenery id. The first rung that
 * answers wins outright — see the header on why the order matters more than the
 * distance.
 */
function findWord(w: string, from: Vec3 | null): Spot | null {
  const exact = nearest((id) => id === w, from)
  if (exact !== null) return exact
  const prop = nearestOf(props, (s) => s.id === w || (s.tags ?? []).includes(w), from)
  if (prop !== null) return prop
  return nearest((id) => id.startsWith(`${w}:`) || id.endsWith(`:${w}`), from)
}

interface Hop {
  to: SceneId
  at: Vec3
  landmarkId: string | null
}

/** Every way OUT of the scene you are standing in, doors and portals alike. */
function exitsFrom(sceneId: SceneId, withLandmarks: boolean): Hop[] {
  const out: Hop[] = []
  const node = world.get(sceneId)
  if (node !== undefined) {
    for (const p of world.portalsIn(sceneId)) {
      out.push({ to: p.toSceneId, at: node.grid.cellToWorld(p.cell), landmarkId: null })
    }
  }
  if (!withLandmarks) return out
  for (const s of places) {
    if (s.toScene === undefined) continue
    out.push({ to: s.toScene as SceneId, at: s.at, landmarkId: s.id })
  }
  return out
}

/**
 * THE DOORS OUT OF A ROOM, as places. Same source the signposts use — any
 * placement whose def is in the `door` category — because a venue is not wired
 * as a portal pair (only the house rooms are; see `wirePortals` in
 * `src/interior/entrance.ts`), so `world.portalsIn('venue_zoo')` is empty and
 * always was. This is what the guide arrow points at when a step names somewhere
 * that is not this room: from inside a room, everywhere else is through the
 * door, and it is a point that is genuinely on this glass.
 */
function roomExits(sceneId: SceneId, from: Vec3 | null): Vec3 | null {
  const node = world.get(sceneId)
  if (node === undefined || node.def.kind === 'exterior') return null
  let best: Vec3 | null = null
  let bestD = Infinity
  for (const p of node.grid.all()) {
    const def = getPlaceable(p.defId as PlaceableId)
    if (def === undefined || def.category !== 'door') continue
    const c = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
    const d = from === null ? 0 : Math.hypot(c.x - from.x, c.z - from.z)
    if (d < bestD) {
      bestD = d
      best = { x: c.x, y: c.y, z: c.z }
    }
  }
  return best
}

/** How many doors deep a route may go before we stop looking. */
const MAX_HOPS = 4

/**
 * THE FIRST DOOR ON THE WAY TO A SCENE — not only a door straight to it.
 *
 * The kitchen is two doors from the street (front door, then the kitchen door),
 * and the reference errand's `make` step says `at: 'house_kitchen'`. Without
 * this the arrow would go quiet the moment a kid stood outside their own house
 * on the one step that most needs pointing at. A shallow breadth-first walk
 * over the scene graph finds the FIRST hop of a route and points at that: from
 * the street it is the front door, and from the living room it is the kitchen
 * door — which is exactly what a kid needs to see at each point.
 *
 * Landmarks (the town's front doors are hotspots, not portals) are only known
 * for the scene you are in, so they seed the first hop and portals do the rest.
 */
function routeTo(active: SceneId, target: SceneId): Hop | null {
  const first = exitsFrom(active, true)
  const direct = first.find((h) => h.to === target)
  if (direct !== undefined) return direct

  const seen = new Set<string>([String(active)])
  // frontier entries carry the FIRST hop that got them there.
  let frontier: Array<{ scene: SceneId; via: Hop }> = first.map((h) => ({ scene: h.to, via: h }))
  for (const f of frontier) seen.add(String(f.scene))

  for (let depth = 1; depth < MAX_HOPS && frontier.length > 0; depth++) {
    const next: Array<{ scene: SceneId; via: Hop }> = []
    for (const f of frontier) {
      for (const h of exitsFrom(f.scene, false)) {
        if (h.to === target) return f.via
        if (seen.has(String(h.to))) continue
        seen.add(String(h.to))
        next.push({ scene: h.to, via: f.via })
      }
    }
    frontier = next
  }
  return null
}

/**
 * THE SEPARATOR IN A CHAINED `at`. See the header: `'house_kitchen>cook_pot'`
 * reads "the kitchen, and once you are in it, the pot". One character, chosen
 * because no scene id, def id, landmark id or tag in this game contains it —
 * `knownPlaceIds()` is the list, and a rung is matched whole.
 */
const CHAIN = '>'

/**
 * The point a step's `at` key means, right now, in the scene the kid is in.
 * Pure read — no state changes, safe to call from a projection pass.
 *
 * `atKey` may be a single place word or a `>`-separated chain (see the header).
 */
export function resolveTarget(atKey: string | null, from: Vec3 | null = null): GuideTarget | null {
  if (atKey === null || atKey === '') return null
  const active = world.activeSceneId
  if (active === null) return null
  ensure()
  if (!atKey.includes(CHAIN)) return resolveOne(atKey, active, from)

  /*
   * A CHAIN. Walk it outward-in and take the first rung with somewhere to point.
   * `here` is remembered rather than returned, so a chain whose every remaining
   * rung is unshipped still answers "you are in the right room" exactly the way
   * the bare key used to — quietly, with no arrow, and never with a wrong one.
   */
  let arrived: GuideTarget | null = null
  for (const rung of atKey.split(CHAIN)) {
    const key = rung.trim()
    if (key === '') continue
    const hit = resolveOne(key, active, from)
    if (hit === null) continue
    if (hit.kind === 'here') {
      arrived = hit
      continue
    }
    return hit
  }
  return arrived
}

function resolveOne(atKey: string, active: SceneId, from: Vec3 | null): GuideTarget | null {
  // (a) already standing in it.
  if (atKey === String(active)) return { kind: 'here', at: null, sceneId: active, landmarkId: null }

  // (b) another scene that exists — point at the FIRST DOOR ON THE WAY there.
  if (world.has(atKey as SceneId)) {
    const hop = routeTo(active, atKey as SceneId)
    if (hop !== null) {
      return { kind: 'door', at: hop.at, sceneId: active, landmarkId: hop.landmarkId }
    }
    /*
     * No route the scene graph knows about — which is the normal case from
     * inside a VENUE, because venues are not wired as portal pairs. The answer
     * is not "say nothing": from inside a room, the way to anywhere at all is
     * out of the door, and the door is on this glass. (Outdoors this still
     * returns null and still says nothing rather than point at a wall.)
     */
    const door = roomExits(active, from)
    if (door !== null) return { kind: 'door', at: door, sceneId: active, landmarkId: null }
    return null
  }

  // (c)-(f) the key itself, then each synonym IN ORDER. One word at a time, so a
  // decorative bloom can never outrank the tree the fruit actually comes from.
  for (const w of [atKey, ...(SYNONYM[atKey] ?? [])]) {
    const hit = findWord(w, from)
    if (hit === null) continue
    return {
      kind: hit.toScene === undefined ? 'place' : 'door',
      at: hit.at,
      sceneId: active,
      landmarkId: hit.id,
    }
  }

  // (g) nothing. No arrow. Not an error — see the header.
  return null
}

let wired = false

/** Drops the cache whenever the world underneath it changes. Idempotent. */
export function installTargets(): void {
  if (wired) return
  wired = true
  const drop = (): void => {
    places = []
    props = []
    cachedScene = null
  }
  bus.on('scene:entered', drop)
  bus.on('save:loaded', drop)
  // A placed thing is a place now (rung d), so the town changing under the arrow
  // has to drop the cache too. All four are one-a-tap events, not per-frame.
  bus.on('build:placed', drop)
  bus.on('build:removed', drop)
  bus.on('build:moved', drop)
  bus.on('build:undo', drop)
}

/** Test surface: every place key the arrow could currently resolve. */
export function knownPlaceIds(): string[] {
  ensure()
  return [...new Set([...places.map((p) => p.id), ...props.map((p) => p.id)])].sort()
}
